/**
 * AssetCard — organism (CES standard)
 * One hardware asset (droplet) as a white card: state, families, current
 * slotting configuration as chips, the capacity visual (memory-proportional
 * blocks with NUMA boundaries), and resource usage bars. Secondary details
 * (physical position, host ID) live behind the info button; running
 * instances behind a collapsible panel.
 * `.asset-card` is a marker class used by the E2E suite.
 */

import { ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { computeAssetSlotting } from '../../services/slotting';
import type { AssetSummary, InstanceSummary } from '../../types/outposts';
import { CapacityVisual } from './CapacityVisual';
import { ResourceBars } from '../molecules/ResourceBars';
import { DataTable, type DataTableColumn } from '../molecules/DataTable';
import { Chip } from '../atoms/Chip';
import { InfoButton } from '../atoms/InfoButton';
import { StatusBadge } from '../atoms/StatusBadge';

const INSTANCE_COLUMNS: DataTableColumn<InstanceSummary>[] = [
  {
    key: 'instanceId',
    header: 'Instance ID',
    render: (row) => <span className="font-mono text-xs">{row.instanceId}</span>,
  },
  { key: 'name', header: 'Name', render: (row) => row.name ?? '—' },
  { key: 'instanceType', header: 'Type' },
  { key: 'accountId', header: 'Account' },
  { key: 'awsServiceName', header: 'Service' },
];

export function AssetCard({
  asset,
  outpostId,
  instances,
}: {
  asset: AssetSummary;
  outpostId: string;
  /** Running instances on this asset; null when the lookup failed. */
  instances: InstanceSummary[] | null;
}) {
  const slotting = useMemo(() => computeAssetSlotting(asset), [asset]);
  const [infoOpen, setInfoOpen] = useState(false);
  const [instancesOpen, setInstancesOpen] = useState(false);

  // Running instances per type → in-use slot indicators in the visual
  const usedByType = useMemo(() => {
    if (!instances) return undefined;
    const counts: Record<string, number> = {};
    for (const instance of instances) {
      counts[instance.instanceType] = (counts[instance.instanceType] ?? 0) + 1;
    }
    return counts;
  }, [instances]);

  return (
    <article
      className="asset-card grid grid-cols-[1fr_auto] items-start gap-4 rounded-lg border border-gray-200 bg-white p-5 shadow transition-shadow hover:shadow-md"
      data-testid={`asset-${asset.assetId}`}
    >
      <div>
        <div className="mb-2 flex items-center gap-2">
          {asset.instanceFamilies.map((family) => (
            <Chip key={family} variant="family">
              {family}
            </Chip>
          ))}
          <p className="m-0 text-gray-900">
            <strong className="font-bold">Asset ID:</strong> <span>{asset.assetId}</span>
          </p>
          <InfoButton
            expanded={infoOpen}
            label={`Details for asset ${asset.assetId}`}
            testId={`info-asset-${asset.assetId}`}
            onClick={() => setInfoOpen(!infoOpen)}
          />
        </div>

        {infoOpen && (
          <dl
            className="mb-3 flex flex-wrap gap-5 rounded border border-gray-200 bg-gray-50 px-4 py-3 animate-[slide-open_0.2s_ease]"
            data-testid={`asset-details-${asset.assetId}`}
          >
            <div>
              <dt className="mb-1 text-xs uppercase tracking-wide text-gray-500">
                Physical position
              </dt>
              <dd className="m-0 text-sm tabular-nums text-gray-900">
                {asset.rackElevation !== undefined ? `U${asset.rackElevation}` : '—'}
              </dd>
            </div>
            <div>
              <dt className="mb-1 text-xs uppercase tracking-wide text-gray-500">Rack</dt>
              <dd className="m-0 text-sm text-gray-900">{asset.rackId}</dd>
            </div>
            {asset.hostId && (
              <div>
                <dt className="mb-1 text-xs uppercase tracking-wide text-gray-500">Host ID</dt>
                <dd className="m-0 text-sm text-gray-900">{asset.hostId}</dd>
              </div>
            )}
          </dl>
        )}

        {/* Without a computable layout the pools (or the empty hint) show here */}
        {!slotting && (
          <div className="flex flex-wrap gap-2">
            {asset.instanceTypeCapacities.length === 0 ? (
              <Chip variant="empty">no capacity configured</Chip>
            ) : (
              asset.instanceTypeCapacities.map((capacity) => (
                <Chip key={capacity.instanceType}>
                  {capacity.instanceType} × {capacity.count}
                </Chip>
              ))
            )}
          </div>
        )}

        {slotting && (
          <div className="mt-3">
            <CapacityVisual layout={slotting.layout} usedByType={usedByType} />
            <div className="mt-3 flex flex-wrap gap-2">
              {asset.instanceTypeCapacities.map((capacity) => (
                <Chip key={capacity.instanceType}>
                  {capacity.instanceType} × {capacity.count}
                </Chip>
              ))}
            </div>
            <ResourceBars
              memoryUsed={slotting.totals.memoryGiB}
              memoryTotal={slotting.familyData.dropletCapacity.memoryGiB}
              vcpusUsed={slotting.totals.vCPUs}
              vcpusTotal={slotting.vcpuCapacity}
              gpusUsed={slotting.totals.gpus}
              gpusTotal={slotting.familyData.hasGPU ? slotting.familyData.dropletCapacity.gpus : 0}
            />
          </div>
        )}

        <button
          type="button"
          className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-gray-500 transition-colors hover:text-orange-600"
          aria-expanded={instancesOpen}
          data-testid={`instances-toggle-${asset.assetId}`}
          onClick={() => setInstancesOpen(!instancesOpen)}
        >
          <ChevronRight
            size={14}
            aria-hidden="true"
            className={`transition-transform ${instancesOpen ? 'rotate-90' : ''}`}
          />
          Instances{instances !== null ? ` (${instances.length})` : ''}
        </button>

        {instancesOpen && (
          <div className="mt-2" data-testid={`instances-panel-${asset.assetId}`}>
            {instances === null ? (
              <p className="m-0 text-sm text-gray-400">Instance data unavailable</p>
            ) : (
              <DataTable
                data={instances}
                columns={INSTANCE_COLUMNS}
                emptyMessage="No instances running on this asset"
                rowTestId={(row) => `instance-${row.instanceId}`}
              />
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-2">
        <StatusBadge state={asset.state} />
        <Link
          to={`/outposts/${outpostId}/assets/${asset.assetId}/edit`}
          className="rounded bg-gray-200 px-3 py-1 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-300"
          data-testid={`edit-asset-${asset.assetId}`}
        >
          Edit
        </Link>
      </div>
    </article>
  );
}
